import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles,
  FileCheck,
  Percent,
  ArrowLeft,
  ClipboardCheck,
  Check,
  Loader2,
  Download,
  Pencil,
  Trash2,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import jsPDF from "jspdf";
import { generatedQuoteWorkTitle } from "@/lib/generated-quote-title";
import { GeneratedQuoteFiles } from "@/components/admin/generated-quote-files";

type QuoteAuditFinding = {
  category: "missing_item" | "incorrect_assumption" | "inconsistency" | "potential_error" | "other";
  severity: "high" | "medium" | "low";
  detail: string;
};

type QuoteAuditResponse = {
  summary: string;
  findings: QuoteAuditFinding[];
  report: string;
};

type QuoteAuditCheckResponse = QuoteAuditResponse & { checkedAt: string };

type ConfirmedFindingsState = { findings: QuoteAuditFinding[]; confirmedAt: string } | null;

const AUDIT_CATEGORY_LABELS: Record<QuoteAuditFinding["category"], string> = {
  missing_item: "Missing Item",
  incorrect_assumption: "Incorrect Assumption",
  inconsistency: "Inconsistency",
  potential_error: "Potential Error",
  other: "Other",
};

function severityBadgeClass(severity: string) {
  if (severity === "high") return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
  if (severity === "medium") return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
  return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200";
}

function FindingEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial?: QuoteAuditFinding;
  onSave: (finding: QuoteAuditFinding) => void;
  onCancel: () => void;
}) {
  const [category, setCategory] = useState<QuoteAuditFinding["category"]>(initial?.category ?? "other");
  const [severity, setSeverity] = useState<QuoteAuditFinding["severity"]>(initial?.severity ?? "medium");
  const [detail, setDetail] = useState(initial?.detail ?? "");

  return (
    <li className="rounded-md border border-primary/40 bg-muted/30 p-2.5 text-sm space-y-2">
      <div className="flex flex-wrap gap-2">
        <Select value={severity} onValueChange={(v) => setSeverity(v as QuoteAuditFinding["severity"])}>
          <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={(v) => setCategory(v as QuoteAuditFinding["category"])}>
          <SelectTrigger className="w-48 h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(AUDIT_CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Textarea
        value={detail}
        onChange={(e) => setDetail(e.target.value)}
        placeholder="Describe the issue..."
        className="text-xs min-h-[70px]"
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs gap-1"
          disabled={!detail.trim()}
          onClick={() => onSave({ category, severity, detail: detail.trim() })}
        >
          <Check className="h-3.5 w-3.5" /> Save
        </Button>
      </div>
    </li>
  );
}

function CheckResultDialog({
  open,
  onOpenChange,
  isPending,
  error,
  rawResult,
  checkedAt,
  confirmedFindings,
  confirmedAt,
  onRerun,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  error: string | null;
  rawResult: QuoteAuditResponse | null;
  checkedAt: string | null;
  confirmedFindings: QuoteAuditFinding[] | null;
  confirmedAt: string | null;
  onRerun: () => void;
  onConfirm: (findings: QuoteAuditFinding[]) => Promise<void>;
}) {
  const { toast } = useToast();
  const [draftFindings, setDraftFindings] = useState<QuoteAuditFinding[]>([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const loadedKeyRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (isPending) return;

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    const key = checkedAt ?? "none";
    if (!justOpened && loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;

    if (confirmedFindings && confirmedAt) {
      setDraftFindings(confirmedFindings);
      setIsConfirmed(true);
    } else if (rawResult) {
      setDraftFindings(rawResult.findings);
      setIsConfirmed(false);
    } else {
      setDraftFindings([]);
      setIsConfirmed(false);
    }
    setEditingIndex(null);
    setIsAdding(false);
  }, [open, isPending, checkedAt, confirmedFindings, confirmedAt, rawResult]);

  const handleRemove = (index: number) => {
    setDraftFindings((prev) => prev.filter((_, i) => i !== index));
    setIsConfirmed(false);
    setEditingIndex(null);
  };

  const handleEditSave = (index: number, updated: QuoteAuditFinding) => {
    setDraftFindings((prev) => prev.map((f, i) => (i === index ? updated : f)));
    setIsConfirmed(false);
    setEditingIndex(null);
  };

  const handleAddSave = (finding: QuoteAuditFinding) => {
    setDraftFindings((prev) => [...prev, finding]);
    setIsConfirmed(false);
    setIsAdding(false);
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(draftFindings);
      setIsConfirmed(true);
      toast({ title: "Confirmed", description: "Findings confirmed — the PDF report is now available." });
    } catch (err) {
      toast({
        title: "Confirm failed",
        description: err instanceof Error ? err.message : "Could not confirm findings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const isEditingAnything = editingIndex !== null || isAdding;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" /> Quote Check
          </DialogTitle>
          <DialogDescription>
            AI audit comparing the original input against the generated output
          </DialogDescription>
        </DialogHeader>

        {isPending && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Running audit...
          </div>
        )}

        {!isPending && error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {!isPending && !error && (rawResult || confirmedFindings) && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs text-muted-foreground">
                  {checkedAt ? `Last checked: ${formatDateTime(checkedAt)}` : null}
                </p>
                {isConfirmed ? (
                  <Badge className="gap-1 text-[10px] bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <ShieldCheck className="h-3 w-3" /> Confirmed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">Not yet confirmed</Badge>
                )}
              </div>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs" onClick={onRerun}>
                <ClipboardCheck className="h-3.5 w-3.5" /> Re-run check
              </Button>
            </div>

            {rawResult?.summary && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Summary</h3>
                <p className="text-sm text-muted-foreground">{rawResult.summary}</p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">Issues Found ({draftFindings.length})</h3>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 h-7 text-xs"
                  disabled={isAdding}
                  onClick={() => {
                    setIsAdding(true);
                    setEditingIndex(null);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" /> Add Issue
                </Button>
              </div>

              {draftFindings.length === 0 && !isAdding ? (
                <p className="text-sm text-muted-foreground">No issues found — output is consistent with the input.</p>
              ) : (
                <ul className="space-y-2">
                  {draftFindings.map((f, i) =>
                    editingIndex === i ? (
                      <FindingEditor
                        key={i}
                        initial={f}
                        onCancel={() => setEditingIndex(null)}
                        onSave={(updated) => handleEditSave(i, updated)}
                      />
                    ) : (
                      <li key={i} className="rounded-md border p-2.5 text-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge className={`text-[10px] ${severityBadgeClass(f.severity)}`}>{f.severity.toUpperCase()}</Badge>
                              <Badge variant="outline" className="text-[10px]">{AUDIT_CATEGORY_LABELS[f.category] ?? f.category}</Badge>
                            </div>
                            <p>{f.detail}</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              disabled={isAdding}
                              onClick={() => {
                                setEditingIndex(i);
                                setIsAdding(false);
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleRemove(i)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </li>
                    ),
                  )}
                  {isAdding && <FindingEditor onCancel={() => setIsAdding(false)} onSave={handleAddSave} />}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t flex-wrap">
              <p className="text-xs text-muted-foreground">
                {isConfirmed
                  ? "Confirmed — the PDF report reflects this list."
                  : "Confirm the check results first to enable the PDF download."}
              </p>
              <Button
                size="sm"
                className="gap-1.5 h-8 text-xs"
                disabled={isConfirming || isEditingAnything}
                onClick={handleConfirm}
              >
                {isConfirming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                {isConfirming ? "Confirming..." : "Confirm Issues"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function useQuoteAuditCheck() {
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [cached, setCached] = useState<QuoteAuditCheckResponse | null>(null);
  const [confirmedFindings, setConfirmedFindings] = useState<QuoteAuditFinding[] | null>(null);
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/generated-quotes/${id}/check`);
      return (await res.json()) as QuoteAuditCheckResponse;
    },
    onSuccess: () => {
      // A fresh AI run always clears any prior confirmation server-side.
      setConfirmedFindings(null);
      setConfirmedAt(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/generated-quotes"] });
      if (currentId) queryClient.invalidateQueries({ queryKey: [`/api/admin/generated-quotes/${currentId}`] });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async ({ id, findings }: { id: string; findings: QuoteAuditFinding[] }) => {
      const res = await apiRequest("POST", `/api/admin/generated-quotes/${id}/confirm-findings`, { findings });
      return (await res.json()) as { confirmedFindings: QuoteAuditFinding[]; confirmedAt: string };
    },
    onSuccess: (data) => {
      setConfirmedFindings(data.confirmedFindings);
      setConfirmedAt(data.confirmedAt);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/generated-quotes"] });
      if (currentId) queryClient.invalidateQueries({ queryKey: [`/api/admin/generated-quotes/${currentId}`] });
    },
  });

  // `cachedResult` lets callers pass a previously-saved audit result (stored on the
  // generation log) so we can show it instantly instead of re-running the AI check.
  // `confirmed` similarly seeds the previously-confirmed findings list, if any.
  const runCheck = (id: string, cachedResult?: QuoteAuditCheckResponse | null, confirmed?: ConfirmedFindingsState) => {
    setCurrentId(id);
    setOpen(true);
    mutation.reset();
    confirmMutation.reset();
    setConfirmedFindings(confirmed?.findings ?? null);
    setConfirmedAt(confirmed?.confirmedAt ?? null);
    if (cachedResult) {
      setCached(cachedResult);
    } else {
      setCached(null);
      mutation.mutate(id);
    }
  };

  const rerun = () => {
    if (!currentId) return;
    setCached(null);
    mutation.mutate(currentId);
  };

  const handleConfirm = async (findings: QuoteAuditFinding[]) => {
    if (!currentId) throw new Error("No active record to confirm findings for");
    await confirmMutation.mutateAsync({ id: currentId, findings });
  };

  const errorMessage = mutation.isError
    ? mutation.error instanceof Error
      ? mutation.error.message
      : "Failed to run audit check. Please try again."
    : null;

  const result = mutation.data ?? cached;

  return {
    runCheck,
    dialog: (
      <CheckResultDialog
        open={open}
        onOpenChange={setOpen}
        isPending={mutation.isPending}
        error={errorMessage}
        rawResult={result}
        checkedAt={result?.checkedAt ?? null}
        confirmedFindings={confirmedFindings}
        confirmedAt={confirmedAt}
        onRerun={rerun}
        onConfirm={handleConfirm}
      />
    ),
  };
}

type GeneratedQuoteLog = {
  id: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  market: string | null;
  quoteClass: string | null;
  searchMode: string | null;
  description: string | null;
  projectTypes: string[];
  customerName: string | null;
  projectAddress: string | null;
  uploadedFileNames: string[];
  uploadedFileKeys: string[];
  uploadedFileUrls: (string | null)[];
  projectType: string | null;
  projectSummary: string | null;
  lineItems: any[];
  lineItemCount: number;
  assumptions: string[];
  exclusions: string[];
  estimatedDays: number | null;
  confidenceLevel: string | null;
  reviewerNotes: string[];
  validationFlags: any[];
  notes: string | null;
  savedQuoteId: string | null;
  savedAt: string | null;
  auditResult: QuoteAuditResponse | null;
  auditCheckedAt: string | null;
  confirmedFindings: QuoteAuditFinding[] | null;
  confirmedAt: string | null;
  pipelineMode: string | null;
  createdAt: string;
};

function cachedAuditFor(log: Pick<GeneratedQuoteLog, "auditResult" | "auditCheckedAt">): QuoteAuditCheckResponse | null {
  if (!log.auditResult || !log.auditCheckedAt) return null;
  return { ...log.auditResult, checkedAt: log.auditCheckedAt };
}

function confirmedFindingsFor(log: Pick<GeneratedQuoteLog, "confirmedFindings" | "confirmedAt">): ConfirmedFindingsState {
  if (!log.confirmedFindings || !log.confirmedAt) return null;
  return { findings: log.confirmedFindings, confirmedAt: log.confirmedAt };
}

function lineItemTotal(item: any): number | null {
  const qty = Number(item.quantity);
  const unitCost = Number(item.unitCost);
  if (isFinite(qty) && isFinite(unitCost)) return qty * unitCost;
  const fallbackTotal = item.subtotal ?? item.totalCost;
  if (fallbackTotal != null && isFinite(Number(fallbackTotal))) return Number(fallbackTotal);
  return null;
}

function formatCostValue(value: unknown): string {
  const num = Number(value);
  return value != null && isFinite(num) ? num.toFixed(2) : "—";
}

function buildGeneratedQuoteReport(log: GeneratedQuoteLog, confirmed: ConfirmedFindingsState): string {
  const lines: string[] = [];
  const push = (s = "") => lines.push(s);

  push(`GENERATED QUOTE REPORT`);
  push(`Record ID: ${log.id}`);
  push(`Generated: ${new Date().toLocaleString("en-GB")}`);
  push("=".repeat(60));
  push();

  push("## Generation Metadata");
  push(`Date: ${formatDateTime(log.createdAt)}`);
  push(`User: ${log.userName || log.userEmail || log.userId}`);
  push(`Market: ${log.market ? (MARKET_LABELS[log.market] ?? log.market) : "—"}`);
  push(`Class / Mode: ${log.quoteClass ?? "—"} / ${log.searchMode ?? "—"}`);
  push(`Confidence: ${log.confidenceLevel ?? "—"}`);
  push(`Customer: ${log.customerName ?? "—"}`);
  push(`Address: ${log.projectAddress ?? "—"}`);
  push(`Line item count: ${log.lineItemCount}`);
  push(`Estimated days: ${log.estimatedDays ?? "—"}`);
  push();

  push("## Project Description (input)");
  push(log.description ? log.description : "—");
  push();

  push("## Project Summary (AI output)");
  push(log.projectSummary ? log.projectSummary : "—");
  push();

  push(`## Line Items (${log.lineItemCount})`);
  if (!log.lineItems || log.lineItems.length === 0) {
    push("No line items");
  } else {
    log.lineItems.forEach((item: any, i: number) => {
      const total = lineItemTotal(item);
      push(
        `${i + 1}. ${item.description ?? item.name ?? "—"} | Category: ${item.category ?? "—"} | Qty: ${item.quantity ?? "—"} | Unit Cost: ${formatCostValue(item.unitCost)} | Total: ${total != null ? formatCostValue(total) : "—"}`,
      );
    });
  }
  push();

  push("## Issues Found");
  if (!confirmed) {
    push("No confirmed check results for this record.");
  } else {
    push(`Confirmed at: ${formatDateTime(confirmed.confirmedAt)}`);
    if (log.auditResult?.summary) {
      push();
      push("Summary:");
      push(log.auditResult.summary);
    }
    push();
    push(`Issues Found (${confirmed.findings.length}):`);
    if (confirmed.findings.length === 0) {
      push("No issues found — output is consistent with the input.");
    } else {
      confirmed.findings.forEach((f, i) => {
        push(`${i + 1}. [${f.severity.toUpperCase()}] ${AUDIT_CATEGORY_LABELS[f.category] ?? f.category}: ${f.detail}`);
      });
    }
  }

  return lines.join("\n");
}

function reportFilenameFor(log: GeneratedQuoteLog, ext: "pdf" = "pdf"): string {
  const dateStr = new Date(log.createdAt).toISOString().slice(0, 10);
  return `generated-quote-report-${dateStr}-${log.id}.${ext}`;
}

function triggerPdfDownload(filename: string, content: string) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;
  const marginTop = 48;
  const marginBottom = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - marginX * 2;
  const bodyFontSize = 9;
  const headingFontSize = 11;
  const titleFontSize = 14;
  const lineHeight = 12;

  let y = marginTop;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - marginBottom) {
      doc.addPage();
      y = marginTop;
    }
  };

  const rawLines = content.split("\n");
  let isFirstLine = true;

  rawLines.forEach((rawLine) => {
    const line = rawLine.trimEnd();

    let fontSize = bodyFontSize;
    let fontStyle: "normal" | "bold" = "normal";

    if (isFirstLine) {
      fontSize = titleFontSize;
      fontStyle = "bold";
      isFirstLine = false;
    } else if (line.startsWith("## ")) {
      fontSize = headingFontSize;
      fontStyle = "bold";
    } else if (/^=+$/.test(line)) {
      // Skip the plain "====" separator rule; PDF uses spacing/weight instead.
      return;
    }

    doc.setFont("helvetica", fontStyle);
    doc.setFontSize(fontSize);

    if (line.length === 0) {
      ensureSpace(lineHeight);
      y += lineHeight;
      return;
    }

    const displayLine = line.startsWith("## ") ? line.slice(3) : line;
    const wrapped = doc.splitTextToSize(displayLine, maxWidth) as string[];
    wrapped.forEach((wrappedLine) => {
      ensureSpace(lineHeight + 2);
      doc.text(wrappedLine, marginX, y);
      y += lineHeight + 2;
    });
  });

  doc.save(filename);
}

type ListResponse = {
  logs: GeneratedQuoteLog[];
  total: number;
  stats: { total: number; saved: number; savedPct: number };
};

function formatDateTime(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const MARKET_LABELS: Record<string, string> = {
  uk: "UK",
  au: "AU",
  ca: "CA",
  us: "US",
};

function DetailView({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, isLoading } = useQuery<GeneratedQuoteLog>({
    queryKey: [`/api/admin/generated-quotes/${id}`],
  });
  const { runCheck, dialog } = useQuoteAuditCheck();
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const canDownload = !!data && !!data.confirmedFindings && !!data.confirmedAt;

  const handleDownload = () => {
    if (!data || !canDownload) return;
    setIsDownloading(true);
    try {
      const report = buildGeneratedQuoteReport(data, confirmedFindingsFor(data));
      triggerPdfDownload(reportFilenameFor(data), report);
    } catch (err) {
      toast({
        title: "Download failed",
        description: err instanceof Error ? err.message : "Could not generate the report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Back to list
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => runCheck(id, data ? cachedAuditFor(data) : null, data ? confirmedFindingsFor(data) : null)}
          >
            <ClipboardCheck className="h-4 w-4" /> {data && cachedAuditFor(data) ? "View Check" : "Check"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={!canDownload || isDownloading}
            title={!canDownload ? "Confirm the check results first" : undefined}
            onClick={handleDownload}
          >
            {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isDownloading ? "Preparing..." : "Download PDF"}
          </Button>
        </div>
      </div>
      {dialog}

      {isLoading || !data ? (
        <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-xl">{generatedQuoteWorkTitle(data)}</CardTitle>
                  <CardDescription>Generation Detail · {formatDateTime(data.createdAt)}</CardDescription>
                </div>
                {data.savedQuoteId ? (
                  <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Saved as quote</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">Not saved</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">User</p>
                <p className="font-medium">{data.userName || data.userEmail || data.userId}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Market</p>
                <p className="font-medium">{data.market ? (MARKET_LABELS[data.market] ?? data.market) : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Class / Mode</p>
                <p className="font-medium capitalize">{data.quoteClass ?? "—"} / {data.searchMode ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pipeline</p>
                <p className="font-medium">
                  {data.pipelineMode === "core"
                    ? <span className="text-blue-600 dark:text-blue-400">Core (3-call)</span>
                    : <span>Standard</span>
                  }
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Confidence</p>
                <p className="font-medium capitalize">{data.confidenceLevel ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">{data.customerName ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Address</p>
                <p className="font-medium">{data.projectAddress ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Line items</p>
                <p className="font-medium">{data.lineItemCount}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Estimated days</p>
                <p className="font-medium">{data.estimatedDays ?? "—"}</p>
              </div>
            </CardContent>
          </Card>

          {data.description && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Project Description (input)</CardTitle></CardHeader>
              <CardContent><p className="text-sm whitespace-pre-wrap">{data.description}</p></CardContent>
            </Card>
          )}

          {data.projectSummary && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Project Summary (AI output)</CardTitle></CardHeader>
              <CardContent><p className="text-sm whitespace-pre-wrap">{data.projectSummary}</p></CardContent>
            </Card>
          )}

          {data.reviewerNotes && data.reviewerNotes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Reviewer Notes ({data.reviewerNotes.length})</CardTitle>
                <CardDescription>Pipeline review-pass notes and flag-only audit checks (e.g. beam price positioning) — prices are never changed by these</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {data.reviewerNotes.map((note, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex gap-2">
                      <span className="text-amber-600 shrink-0">•</span>
                      <span className="whitespace-pre-wrap">{note}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <GeneratedQuoteFiles id={data.id} names={data.uploadedFileNames || []} keys={data.uploadedFileKeys || []} />

          <Card>
            <CardHeader><CardTitle className="text-sm">Line Items ({data.lineItemCount})</CardTitle></CardHeader>
            <CardContent>
              {data.lineItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No line items</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Description</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs text-right">Qty</TableHead>
                        <TableHead className="text-xs text-right">Unit Cost</TableHead>
                        <TableHead className="text-xs text-right">Total Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.lineItems.map((item: any, i: number) => {
                        const qty = Number(item.quantity);
                        const unitCost = Number(item.unitCost);
                        const hasTotal = isFinite(qty) && isFinite(unitCost);
                        const fallbackTotal = item.subtotal ?? item.totalCost;
                        const total = hasTotal
                          ? qty * unitCost
                          : fallbackTotal != null && isFinite(Number(fallbackTotal))
                            ? Number(fallbackTotal)
                            : null;
                        const formatCost = (value: unknown) => {
                          const num = Number(value);
                          return value != null && isFinite(num) ? num.toFixed(2) : "—";
                        };
                        return (
                          <TableRow key={i}>
                            <TableCell className="text-xs">{item.description ?? item.name ?? "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{item.category ?? "—"}</TableCell>
                            <TableCell className="text-xs text-right">{item.quantity ?? "—"}</TableCell>
                            <TableCell className="text-xs text-right">{formatCost(item.unitCost)}</TableCell>
                            <TableCell className="text-xs text-right">{total != null ? formatCost(total) : "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="text-xs font-semibold" colSpan={4}>Total</TableCell>
                        <TableCell className="text-xs font-semibold text-right">
                          {(() => {
                            const sum = data.lineItems.reduce((acc: number, item: any) => {
                              const t = lineItemTotal(item);
                              return t != null ? acc + t : acc;
                            }, 0);
                            return `£${sum.toFixed(2)}`;
                          })()}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default function AdminGeneratedQuotesPage() {
  const [, setLocation] = useLocation();
  const [savedFilter, setSavedFilter] = useState("all");
  const [marketFilter, setMarketFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params = new URLSearchParams();
  if (savedFilter !== "all") params.set("savedFilter", savedFilter);
  if (marketFilter !== "all") params.set("market", marketFilter);
  const queryString = params.toString();

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: [`/api/admin/generated-quotes${queryString ? `?${queryString}` : ""}`],
  });
  const { runCheck, dialog } = useQuoteAuditCheck();

  if (selectedId) {
    return <DetailView id={selectedId} onBack={() => setSelectedId(null)} />;
  }

  const stats = data?.stats;
  const logs = data?.logs ?? [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-lg sm:text-2xl font-bold">Generated Quotes</h1>
        <p className="text-muted-foreground text-sm">Every AI quote generation, whether or not the user saved it</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Total Generations</p>
              <p className="text-2xl font-bold">{stats?.total ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <FileCheck className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Saved as Quote</p>
              <p className="text-2xl font-bold">{stats?.saved ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Percent className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Save Rate</p>
              <p className="text-2xl font-bold">{stats ? `${stats.savedPct}%` : "—"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Generation Records</CardTitle>
          <CardDescription>Filter and review all AI quote generations</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select value={savedFilter} onValueChange={setSavedFilter}>
              <SelectTrigger className="w-44 h-8 text-xs">
                <SelectValue placeholder="Saved status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All generations</SelectItem>
                <SelectItem value="saved">Saved as quote</SelectItem>
                <SelectItem value="unsaved">Not saved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={marketFilter} onValueChange={setMarketFilter}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue placeholder="Market" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All markets</SelectItem>
                <SelectItem value="uk">UK</SelectItem>
                <SelectItem value="au">AU</SelectItem>
                <SelectItem value="ca">CA</SelectItem>
                <SelectItem value="us">US</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No generations match the current filters</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">User</TableHead>
                    <TableHead className="text-xs">Market</TableHead>
                    <TableHead className="text-xs">Class</TableHead>
                    <TableHead className="text-xs">Mode</TableHead>
                    <TableHead className="text-xs">Project Type</TableHead>
                    <TableHead className="text-xs text-right">Items</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedId(log.id)}
                    >
                      <TableCell className="text-xs text-muted-foreground">{formatDateTime(log.createdAt)}</TableCell>
                      <TableCell className="text-xs font-medium">{log.userName || log.userEmail || log.userId}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">{log.market ? (MARKET_LABELS[log.market] ?? log.market) : "—"}</Badge>
                      </TableCell>
                      <TableCell className="text-xs capitalize">{log.quoteClass ?? "—"}</TableCell>
                      <TableCell>
                        {log.pipelineMode === "core"
                          ? <Badge className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Core</Badge>
                          : <Badge variant="outline" className="text-xs text-muted-foreground">Standard</Badge>
                        }
                      </TableCell>
                      <TableCell className="text-xs">{log.projectType ?? "—"}</TableCell>
                      <TableCell className="text-xs text-right">{log.lineItemCount}</TableCell>
                      <TableCell>
                        {log.savedQuoteId
                          ? <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Saved</Badge>
                          : <Badge variant="outline" className="text-xs">Not saved</Badge>
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            runCheck(log.id, cachedAuditFor(log), confirmedFindingsFor(log));
                          }}
                        >
                          <ClipboardCheck className="h-3.5 w-3.5" /> {cachedAuditFor(log) ? "View Check" : "Check"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {dialog}
    </div>
  );
}
